import config from 'favoris-web/config/environment';
import type { RequestContext } from '@warp-drive/core/types/request';
import type { Handler, NextFn } from '@warp-drive/core/request';

/**
 * Handler that prepends the configured API host to relative URLs.
 * This ensures requests go to the correct API server in production builds.
 *
 * For development with Vite proxy, apiHost will be empty and requests
 * will be made relative to the current origin (handled by Vite proxy).
 * For production or when FAVORIS_API_HOST is set, all relative URLs
 * are prefixed with the API host.
 */
export const ApiHostHandler: Handler = {
  request<T>(context: RequestContext, next: NextFn<T>) {
    const url = context.request.url as string;
    const apiHost = config.favoris.apiHost || null;
    const ns = config.favoris.apiNamespace;

    // Only modify relative URLs (not absolute URLs)
    if (apiHost && !url.startsWith('http')) {
      // URL already starts with the namespace prefix, just prepend host
      if (url.startsWith(`/${ns}/`)) {
        const updatedUrl = `${apiHost}${url}`;
        return next(Object.assign({}, context.request, { url: updatedUrl }));
      }
      // URL starts with / but not the namespace prefix, add it
      if (url.startsWith('/')) {
        const updatedUrl = `${apiHost}/${ns}${url}`;
        return next(Object.assign({}, context.request, { url: updatedUrl }));
      }
      // Bare path — add leading slash and namespace
      const updatedUrl = `${apiHost}/${ns}/${url}`;
      return next(Object.assign({}, context.request, { url: updatedUrl }));
    }

    return next(context.request);
  },
};
